import React, { useCallback } from 'react';
import { Repeat } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import ReportWidget from './components/ReportWidget';
import CustomTooltip from './components/CustomTooltip';
import { DataPoint, PercentagePoint } from './components/DataPoint';
import { useTranslation } from 'react-i18next';

// Colors for the chart
const COLORS = {
  repeating: '#3b82f6',    // Blue
  nonRepeating: '#9ca3af', // Gray
};

/**
 * Summary row displaying Non-Repeating, Repeating counts and repeating percentage
 */
const SummaryRow = ({
  nonRepeatingCount,
  repeatingCount,
  repeatingPercentage,
  onNonRepeatingClick,
  onRepeatingClick
}) => {
  const { t } = useTranslation();
  return (
  <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-3 gap-md-5 mb-3 py-2">
    <DataPoint
      value={nonRepeatingCount}
      label={t('shared.non_repeating', 'Non-Repeating')}
      color={COLORS.nonRepeating}
      onClick={onNonRepeatingClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <DataPoint
      value={repeatingCount}
      label={t('shared.repeating', 'Repeating')}
      color={COLORS.repeating}
      onClick={onRepeatingClick}
    />

    <div className="border-end d-none d-md-block" style={{ height: '60px' }} />

    <PercentagePoint
      value={repeatingPercentage.toFixed(1)}
      label={t('reporting.repeating_report.ratio', 'Repeating Ratio')}
    />
  </div>
);
}

/**
 * RepeatingReportWidget - Displays non-repeating vs repeating work orders
 *
 * Shows:
 * - Summary counts for non-repeating and repeating work orders
 * - Repeating percentage
 * - Stacked bar chart showing trends over time
 */
const RepeatingReportWidget = ({
  data,
  loading,
  error,
  onNonRepeatingClick,
  onRepeatingClick,
  onChartBarClick,
  onDetailsClick
}) => {
  const { t } = useTranslation();
  const { summary, chartData } = data || {};
  const {
    nonRepeatingCount = 0,
    repeatingCount = 0,
    repeatingPercentage = 0
  } = summary || {};
  const isEmpty = !chartData || chartData.length === 0;

  // Handle bar click
  const handleBarClick = useCallback((data, isRepeating) => {
    if (onChartBarClick && data && data.payload) {
      onChartBarClick(data.payload, isRepeating);
    }
  }, [onChartBarClick]);

  return (
    <ReportWidget
      title={t('reporting.repeating_report.title', 'Non-Repeating vs. Repeating')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={Repeat}
      emptyMessage={t('shared.no_data_available', 'No data available for the selected period.')}
    >
      {/* Summary Data Points */}
      <SummaryRow
        nonRepeatingCount={nonRepeatingCount}
        repeatingCount={repeatingCount}
        repeatingPercentage={repeatingPercentage}
        onNonRepeatingClick={onNonRepeatingClick}
        onRepeatingClick={onRepeatingClick}
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData || []}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="periodLabel"
            stroke="#666"
            tick={{ fontSize: 12 }}
          />
          <YAxis stroke="#666" tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar
            dataKey="repeatingCount"
            stackId="a"
            fill={COLORS.repeating}
            name={t('shared.repeating', 'Repeating')}
            cursor="pointer"
            onClick={(data) => handleBarClick(data, true)}
          />
          <Bar
            dataKey="nonRepeatingCount"
            stackId="a"
            fill={COLORS.nonRepeating}
            name={t('shared.non_repeating', 'Non-Repeating')}
            cursor="pointer"
            onClick={(data) => handleBarClick(data, false)}
          />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default RepeatingReportWidget;
